import React, { ReactNode } from 'react';
import { Button } from 'components/common/Button';
import { FormErrorMessage } from 'components/common/form-utils/form-elements';
import { FieldState, FormState } from 'components/common/form-utils/form-state';
import Rating from 'components/common/Rating';
import formatPrice from 'utils/format-price';
import { Product } from 'utils/Product';
import ProductFieldEditor from './ProductFieldEditor';
import { NewProduct } from 'api-services/product-requests';

const FieldEditorRow = ({
  fieldName, field, baseProduct,
}: {
  fieldName: keyof NewProduct,
  field: FieldState
  baseProduct: Product,
}) => {
  const isEditing = field.value !== undefined;

  const FieldView = () => {
    const labelProp = { 'aria-label': `Current value of field ${fieldName}` };
    if (fieldName === 'image') {
      return <img
        {...labelProp}
        src={baseProduct.image}
        alt={baseProduct.title}
        title={baseProduct.title}
        className='h-12 w-12 rounded-md border border-gray-400' />;
    } else if (fieldName === 'stars') {
      return <span {...labelProp}>
        <Rating readonly className='align-middle' size={13} ratingValue={baseProduct.stars} />
      </span>;
    } else if (fieldName === 'price') {
      return <span {...labelProp}>{formatPrice(baseProduct[fieldName])}</span>;
    } else if (fieldName === 'visible') {
      return <input {...labelProp} type='checkbox' readOnly checked={baseProduct[fieldName]} />;
    }
    return <span {...labelProp}>{baseProduct[fieldName].toString()}</span>;
  };

  return <tr className='h-12'>
    <td className='align-middle'>
      <label htmlFor={`product_${fieldName}_row`}>{fieldName[0].toUpperCase() + fieldName.slice(1)}</label>
    </td>

    <td aria-label={`Current value of field ${fieldName}`}>
      <FieldView />
    </td>

    <td className='grid justify-items-center mt-1'>
      {!isEditing && <Button
        primary
        data-locator='init-edit-button'
        className='w-16'
        onClick={() => field.setValue(baseProduct[fieldName].toString())}>Edit</Button>}
      {isEditing && <Button data-locator={'cancel-edit-button'} danger className='w-16' onClick={() => {
        field.setValue(undefined);
        field.setError(undefined);
      }}>Cancel</Button>}
    </td>

    <td className='text-center pt-1'>
      {!isEditing && <span className='italic text-gray-400'>Unedited</span>}
      {isEditing && <ProductFieldEditor fieldName={fieldName} field={field} />}
      {field.error && <FormErrorMessage>{field.error}</FormErrorMessage>}
    </td>
  </tr>;
};

const ProductEditorTable = (
  { baseProduct, productEdits }: { baseProduct: Product, productEdits: FormState },
) => {
  const ColumnHeader = ({ children, className }: { children: ReactNode, className?: string }) => (
    <th
      scope='col'
      className={
        `px-2 py-1 md:px-6 md:py-3 text-xs md:text-s text-center text-gray-500 uppercase tracking-wider ${className}`
      }>{children}</th>
  );

  return <table className='divide-y divide-gray-200 mt-4 mb-8'>
    <thead className='bg-gray-50'>
      <tr>
        <ColumnHeader className='w-28'>Field</ColumnHeader>
        <ColumnHeader className='w-48'>Current Value</ColumnHeader>
        <ColumnHeader className='w-24'>Edit</ColumnHeader>
        <ColumnHeader className='w-56'>Modified Value</ColumnHeader>
      </tr>
    </thead>
    <tbody className='text-left'>
      <FieldEditorRow baseProduct={baseProduct} fieldName='title' field={productEdits.title} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='category' field={productEdits.category} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='image' field={productEdits.image} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='stars' field={productEdits.stars} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='price' field={productEdits.price} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='quantity' field={productEdits.quantity} />
      <FieldEditorRow baseProduct={baseProduct} fieldName='visible' field={productEdits.visible} />
    </tbody>
  </table>;
};

export default ProductEditorTable;
